import { type ReactElement, cloneElement } from 'react';

import type { DataTableV2SelectedRowsActionsProps } from './DataTableV2SelectedRowsActions.js';
import type { DataTableV2TableActionsProps } from './DataTableV2TableActions.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import type { DataTableV2RowData } from '../../public.api.js';

// #region Typings
type DataTableV2ActionsWrapperProps<TData extends DataTableV2RowData> = {
  tableActions: ReactElement<DataTableV2TableActionsProps> | null;
  selectedRowsActions: ReactElement<
    DataTableV2SelectedRowsActionsProps<TData>
  > | null;
};
// #endregion Typings

/**
 * This component is used to conditionally render TableActions and SelectedRowsActions
 * and toggle their visibility state
 * @internal
 */
export const DataTableV2TableActionsWrapper = <
  TData extends DataTableV2RowData,
>(
  props: DataTableV2ActionsWrapperProps<TData>,
) => {
  const { tableActions, selectedRowsActions } = props;
  const table = useDataTableV2();
  const showSelectedRowsActions =
    selectedRowsActions && table.getSelectionCount() > 0;

  if (tableActions === null && selectedRowsActions === null) {
    return null;
  }

  return (
    <>
      {selectedRowsActions &&
        cloneElement(selectedRowsActions, {
          style: {
            visibility: showSelectedRowsActions ? 'visible' : 'hidden',
            gridArea: ' 1 / 1 / 2 / 2',
            ...selectedRowsActions.props.style,
          },
        })}
      {tableActions &&
        cloneElement(tableActions, {
          style: {
            visibility:
              !showSelectedRowsActions && tableActions ? 'visible' : 'hidden',
            gridArea: ' 1 / 1 / 2 / 2',
            ...tableActions.props.style,
          },
        })}
    </>
  );
};
